import { z } from "zod";

import type { RiskControlKey } from "~/v3-poc/api/riskControls";
import type { RiskControls } from "~/v3-poc/api/riskControls.schema";

/**
 * The controls are mutually exclusive, so the account is in exactly one of
 * three states. Modelling that as one selection rather than two booleans makes
 * the illegal "both active" combination unrepresentable instead of merely
 * rejected.
 */
export type RiskControlSelection = RiskControlKey | null;

export interface RiskControlsFormValues {
  selection: RiskControlSelection;
}

export const RISK_CONTROL_KEYS = [
  "suspendTrading",
  "liquidationOnly",
] as const satisfies readonly RiskControlKey[];

export interface RiskControlMeta {
  label: string;
  /** What the control does while it is active. */
  description: string;
  /** Title of the confirmation shown when this control is already active. */
  liftTitle: string;
  /** What lifting it will do. */
  liftEffect: string;
  /** Confirm-button copy for lifting it. */
  liftConfirmLabel: string;
}

export const RISK_CONTROL_META: Record<RiskControlKey, RiskControlMeta> = {
  suspendTrading: {
    label: "Suspend trading",
    description: "Blocks all new order entry.",
    liftTitle: "Resume trading?",
    liftEffect: "New order entry will be accepted again.",
    liftConfirmLabel: "Resume trading",
  },
  liquidationOnly: {
    label: "Liquidation only",
    description: "Only position-reducing orders are accepted.",
    liftTitle: "Lift liquidation only?",
    liftEffect: "Normal order entry will be resumed.",
    liftConfirmLabel: "Lift restriction",
  },
};

/**
 * `looseParse` hands back the raw payload when validation fails, so a boolean
 * can be `undefined` at runtime despite its type. Coerce rather than trust it.
 *
 * Should the backend ever report both flags set — nothing in this UI can
 * produce that — the harder restriction wins, and lifting it reveals the other.
 */
export const toRiskControlSelection = (
  controls: RiskControls | undefined,
): RiskControlSelection => {
  if (controls?.suspend_trading === true) {
    return "suspendTrading";
  }
  if (controls?.liquidation_only === true) {
    return "liquidationOnly";
  }
  return null;
};

/**
 * An unrestricted account picks a restriction to apply; a restricted one has
 * exactly one action available, so that becomes a confirmation rather than a
 * form the user has to un-fill first.
 */
export type RiskControlsDialogMode = "select" | "lift";

export const getRiskControlsDialogMode = (
  current: RiskControlSelection,
): RiskControlsDialogMode => (current === null ? "select" : "lift");

/**
 * Liquidate-all is an operation rather than a flag, so it takes over the dialog
 * body instead of joining the control list. Kept as a view swap rather than a
 * nested dialog, which `AlertDialog` handles poorly.
 */
export type RiskControlsDialogView = "controls" | "liquidate";

export interface RiskControlChange {
  control: RiskControlKey;
  label: string;
  /** Target state of the control. */
  enabled: boolean;
}

/**
 * At most one change: the only reachable transitions are `none -> X` and
 * `X -> none`, so a submission is always exactly one PUT.
 */
export const getRiskControlChange = (
  current: RiskControlSelection,
  next: RiskControlSelection,
): RiskControlChange | null => {
  if (current === next) {
    return null;
  }

  const control = next ?? current;
  if (control === null) {
    return null;
  }

  return {
    control,
    label: RISK_CONTROL_META[control].label,
    enabled: next !== null,
  };
};

export const riskControlsFormSchema = z.object({
  selection: z.enum(RISK_CONTROL_KEYS).nullable(),
});

export type RiskControlsBadgeColor = "gray" | "orange" | "red";

export interface RiskControlsSummary {
  label: string;
  /**
   * Two-letter form for the collapsed left rail, where the full label does not
   * fit. Chosen copy rather than initials derived at render time, so a label
   * reword cannot silently produce a meaningless pair of letters.
   */
  shortLabel: string;
  color: RiskControlsBadgeColor;
  isRestricted: boolean;
}

/** Drives the header button's label and colour. */
export const getRiskControlsSummary = (
  selection: RiskControlSelection,
): RiskControlsSummary => {
  switch (selection) {
    case "suspendTrading":
      return {
        label: "Trading suspended",
        shortLabel: "TS",
        color: "red",
        isRestricted: true,
      };
    case "liquidationOnly":
      return {
        label: "Liquidation only",
        shortLabel: "LO",
        color: "orange",
        isRestricted: true,
      };
    default:
      return {
        label: "Risk controls",
        shortLabel: "RC",
        color: "gray",
        isRestricted: false,
      };
  }
};
